import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useFarmFields } from '@/modules/Farms/Farms.api';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export interface FieldNavigatorProps {
  farmId: string;
  currentFieldId: string;
}

export const FieldNavigator: React.FC<FieldNavigatorProps> = ({ farmId, currentFieldId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: farmFields = [], status } = useFarmFields(farmId);
  const basePath = location.pathname.replace(new RegExp(`/fields/${currentFieldId}$`), '');
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const idx = farmFields.findIndex((f) => f.field_id === currentFieldId);
    setCurrentIndex(idx >= 0 ? idx : 0);
  }, [farmFields, currentFieldId]);

  const goToIndex = (idx: number) => {
    const f = farmFields[idx];
    if (f) navigate(`${basePath}/fields/${f.field_id}`);
  };

  if (status !== 'success' || farmFields.length <= 1) return null;

  return (
    <div className="flex items-center gap-5 w-72 h-14">
      <Button
        size="icon"
        className="p-0 w-12 h-14 bg-brand-400 border border-brand-400 rounded-md text-white"
        onClick={() => goToIndex((currentIndex - 1 + farmFields.length) % farmFields.length)}
      >
        <ChevronLeft className="w-6 h-6" />
      </Button>

      <Select
        value={farmFields[currentIndex]?.field_id}
        onValueChange={(val) => {
          const idx = farmFields.findIndex((f) => f.field_id === val);
          if (idx >= 0) goToIndex(idx);
        }}
      >
        <SelectTrigger className="flex items-center justify-between w-56 h-14 px-3 py-2 gap-2 bg-white border border-gray-200 rounded-md text-sm font-medium leading-5 text-gray-900">
          <div className="flex-1 flex justify-center min-w-0">
            <div className="max-w-full text-center truncate">
              <SelectValue placeholder="Select field" />
            </div>
          </div>
        </SelectTrigger>
        <SelectContent>
          {farmFields.map((f) => (
            <SelectItem
              key={f.field_id}
              value={f.field_id}
              className={`${f.customer_field_name ? 'justify-start pl-6' : 'justify-center pl-0'}`}
            >
              {f.customer_field_name ? `${f.field_num} - ${f.customer_field_name}` : f.field_num}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Button
        size="icon"
        className="p-0 w-12 h-14 bg-brand-400 border border-brand-400 rounded-md text-white"
        onClick={() => goToIndex((currentIndex + 1) % farmFields.length)}
      >
        <ChevronRight className="w-6 h-6" />
      </Button>
    </div>
  );
};
